import React, { useState, useEffect, useRef } from "react";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  visitedNodeStyle,
  visitedDirectedEdgeStyle,
  visitingNodeStyle,
  visitingDirectedWeightedEdgeStyle,
} from "./GraphStyle";

const DAGS = ({ cytoscape, nodes }) => {
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [allVisitedNodes, setAllVisitedNodes] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [distances, setDistances] = useState({});
  const [parentNodes, setParentNodes] = useState({});
  const [stack, setStack] = useState([]);
  const [topologicalSortComplete, setTopologicalSortComplete] = useState(false);
  const [topologicalSortOrder, setTopologicalSortOrder] = useState([]);
  const [buttonLabel, setButtonLabel] = useState("Animate Topological Sort");
  const [isAnimateTSCompleted, setIsAnimateTSCompleted] = useState(false);
  const [isDAGShortestPathCompleted, setisDAGShortestPathCompleted] =
    useState(false);
  const [isSetInitialsCompleted, setInitialsCompleted] = useState(false);
  const distancesArrayRef = useRef({});
  const parentNodesArrayRef = useRef({});
  distancesArrayRef.current = {};
  parentNodesArrayRef.current = {};
  //const distancesArray = {};
  //const parentNodesArray = {};
  useEffect(() => {
    // Log the "Topological Sort Order" whenever topological sort is complete
    if (topologicalSortComplete) {
      const topologicalSortOrder = [...allVisitedNodes].reverse();
      setTopologicalSortOrder(topologicalSortOrder);
      //console.log("Topological Sort Order:", topologicalSortOrder);
    }
  }, [topologicalSortComplete, allVisitedNodes]);
  let visitedNodeCount = 0;
  let visitedNodesSet = new Set();

  const handleButtonClick = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (buttonLabel === "Animate Topological Sort") {
      await animateTS();
      setButtonLabel("Reset Graph and Set Initial Distances");
    } else if (buttonLabel === "Reset Graph and Set Initial Distances") {
      setInitials(cytoscape);
      setButtonLabel("Find DAG Shortest Path");
      setIsAnimateTSCompleted(false);
    } else if (buttonLabel === "Find DAG Shortest Path") {
      dags(cytoscape);
      setButtonLabel("Reset Animation");
      setisDAGShortestPathCompleted(false);
    } else if (buttonLabel === "Reset Animation") {
      //console.log("Reset Animation button clicked");
      resetAnimation();
      setButtonLabel("Animate Topological Sort");
    }
    setIsAnimating(false);
  };
  const topologicalSort = async (
    startNode,
    parentNode = null,
    visitedNodesSet,
    stack
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const currentNode = cytoscape.getElementById(startNode.data.id);
    currentNode.style(visitingNodeStyle);

    let adjacentEdges = Array.from(
      cytoscape.edges(`[source="${startNode.data.id}"]`)
    );

    adjacentEdges.sort((edgeA, edgeB) => {
      const idA = edgeA.data("id");
      const idB = edgeB.data("id");
      return idB.localeCompare(idA);
    });

    for (const edge of adjacentEdges) {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      const adjacentNodeId =
        sourceId === startNode.data.id ? targetId : sourceId;
      if (adjacentNodeId !== parentNode) {
        const adjacentNode = nodes.find(
          (node) => node.data.id === adjacentNodeId
        );
        if (!visitedNodesSet.has(adjacentNode.data.id)) {
          stack.push(adjacentNode.data.id);
          setStack([...stack]);
          visitedNodesSet.add(adjacentNode.data.id);
          setVisitedNodes([...visitedNodes, adjacentNode.data.id]);
          edge.style(visitedDirectedEdgeStyle);
          await topologicalSort(
            adjacentNode,
            startNode.data.id,
            visitedNodesSet,
            stack
          );
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));
    stack.pop(currentNode);
    setStack([...stack]);
    currentNode.style(visitedNodeStyle);
    //console.log(currentNode.data.id);
    setAllVisitedNodes((prevVisitedNodes) => [
      ...prevVisitedNodes,
      startNode.data.id,
    ]);
  };

  const animateTS = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const sortedNodes = [...nodes].sort((a, b) =>
      b.data.id.localeCompare(a.data.id)
    );

    for (const startNode of sortedNodes) {
      if (!visitedNodesSet.has(startNode.data.id)) {
        stack.push(startNode.data.id);
        setStack([...stack]);
        visitedNodesSet.add(startNode.data.id);
        setVisitedNodes([...visitedNodes, startNode.data.id]);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await topologicalSort(
          startNode,
          visitedNodeCount,
          visitedNodesSet,
          stack
        );
      }
    }
    setTopologicalSortComplete(true);
    setIsAnimateTSCompleted(true);
    setIsAnimating(false);
  };

  const resetAnimation = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    // Reset styles of all nodes and edges
    cytoscape.elements("node").style(initialNodeStyle);

    // Reset styles of all edges to initialEdgeStyleToApply
    cytoscape.elements("edge").style(initialDirectedEdgeStyle);

    // Reset the state
    setVisitedNodes([]);
    setAllVisitedNodes([]);
    setStack([]);
    visitedNodesSet.clear();
    setDistances([]);
    setParentNodes([]);
    setIsAnimating(false);
  };

  const setInitials = (cytoscape) => {
    if (isAnimating) return;

    // Reset styles of all nodes and edges
    cytoscape.elements("node").style(initialNodeStyle);

    // Reset styles of all edges to initialEdgeStyleToApply
    cytoscape.elements("edge").style(initialDirectedEdgeStyle);

    const distancesArray = {};
    const parentNodesArray = {};
    for (const node of nodes) {
      distancesArray[node.data.id] = Infinity;
      parentNodesArray[node.data.id] = null;
    }
    distancesArray[topologicalSortOrder[0]] = 0;

    setDistances({ ...distancesArray });
    setParentNodes({ ...parentNodesArray });
    setInitialsCompleted(true);
    setIsAnimating(false);
  };

  const dags = async (cytoscape) => {
    const updatedDistances = { ...distances };
    const updatedParentNodes = { ...parentNodes };

    // Iterate through nodes in topological order and update distances
    //topologicalSortOrder.forEach((currentNodeId) => {
    for (const currentNodeId of topologicalSortOrder) {
      const currentNode = cytoscape.getElementById(currentNodeId);
      const adjacentEdges = Array.from(
        cytoscape.edges(`[source="${currentNodeId}"]`)
      );
      //console.log(adjacentEdges);

      // Highlight the current node being processed
      currentNode.style(visitingNodeStyle);

      // Wait for a short delay to visualize the process
      await new Promise((resolve) => setTimeout(resolve, 2000));

      //adjacentEdges.forEach((edge) => {
      for (const edge of adjacentEdges) {
        const targetNodeId = edge.target().id();
        const targetNode = cytoscape.getElementById(targetNodeId);
        const edgeWeight = edge.data("weight");
        const newDistance =
          Number(updatedDistances[currentNodeId]) + Number(edgeWeight);
        //console.log(newDistance);

        // Highlight the current edge being processed
        edge.style(visitingDirectedWeightedEdgeStyle);
        targetNode.style(visitingNodeStyle);

        // Wait for a short delay to visualize the process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Update distance if the new distance is smaller
        if (newDistance < updatedDistances[targetNodeId]) {
          updatedDistances[targetNodeId] = newDistance;
          updatedParentNodes[targetNodeId] = currentNodeId;
        }
        //console.log(updatedDistances);
      }
    }
    setDistances({ ...updatedDistances });
    setParentNodes({ ...updatedParentNodes });

    // Wait for a short delay to visualize the process
    await new Promise((resolve) => setTimeout(resolve, 2000));

    Object.keys(updatedParentNodes).forEach((nodeId) => {
      let currentNodeId = nodeId;
      while (currentNodeId !== null) {
        const currentNode = cytoscape.getElementById(currentNodeId);
        currentNode.style({
          ...visitedNodeStyle,
        });
        const parentNodeId = updatedParentNodes[currentNodeId];
        if (parentNodeId !== null) {
          const edge = cytoscape.edges(
            `[source="${parentNodeId}"][target="${currentNodeId}"]`
          );
          edge.style({
            ...visitedDirectedEdgeStyle,
          });
        }
        currentNodeId = parentNodeId;
      }
    });

    //console.log("DAG Shortest Path Distances:", updatedDistances);

    // Set the state to indicate that the operation is done
    setisDAGShortestPathCompleted(true);
    setIsAnimating(false);
  };

  return (
    <div className="topological-sort-container">
      <div className="topological-sort-buttons">
        <button
          className="generate-reset-button"
          onClick={handleButtonClick}
          disabled={
            isAnimating ||
            (!isAnimateTSCompleted &&
              buttonLabel === "Reset Graph and Set Initial Distances") ||
            (!isSetInitialsCompleted &&
              buttonLabel === "Find DAG Shortest Path") ||
            (!isDAGShortestPathCompleted && buttonLabel === "Reset Animation")
          }
        >
          {isAnimating ? "Animating..." : buttonLabel}
        </button>
        &nbsp;&nbsp;
        {/*<button
          className="generate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating}
        >
          Reset Animation
            </button>*/}
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Stack:</div> &nbsp;
          <ul className="stack">
            {[...stack].reverse().map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Topological Sort Order:</div>
          <ul className="stack">
            {[...allVisitedNodes].reverse().map((visitedNode, index) => (
              <li key={index}>{visitedNode}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="table-container">
        <table className="custom-table">
          <tbody>
            <tr>
              <th>Node</th>
              {Object.keys(distances)
                .sort()
                .map((nodeId) => (
                  <td key={nodeId}>{nodeId}</td>
                ))}
            </tr>
            <tr>
              <th>Distance</th>
              {Object.keys(distances)
                .sort()
                .map((nodeId) => (
                  <td key={nodeId}>{distances[nodeId]}</td>
                ))}
            </tr>
            <tr>
              <th>Parent Node</th>
              {Object.keys(parentNodes)
                .sort()
                .map((nodeId) => (
                  <td key={nodeId}>
                    {parentNodes[nodeId] !== null
                      ? parentNodes[nodeId]
                      : "null"}
                  </td>
                ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DAGS;
